import { limit, where } from 'firebase/firestore'
import { FirestoreRepository } from '@upadelmatch/core'
import {
  ContactSchema,
  contactDocId,
  type ContactDoc,
  type ContactStatus,
} from '@/core/models/contact'

/** Bound on one player's saved list. Well above any real roster. */
export const CONTACTS_PAGE_SIZE = 500

// `contacts` — the one-way saved list (data-model § contacts). Owner-only by
// rules; deterministic `${owner}_${contact}` ids make save/block idempotent.
class ContactsRepository extends FirestoreRepository<typeof ContactSchema> {
  constructor() {
    super('contacts', ContactSchema)
  }

  /** Bounded: `contacts` only ever grows, and an unbounded read grows with it. */
  listMine(ownerUid: string, max: number = CONTACTS_PAGE_SIZE): Promise<ContactDoc[]> {
    return this.list(where('ownerUid', '==', ownerUid), limit(max))
  }

  /**
   * Save (or re-activate).
   *
   * ⚠️ NEVER downgrades an explicit block, and this used to be a comment rather
   * than behaviour: `save` was a merge-set writing `status: 'active'`, so
   * saving a player you had blocked silently UN-BLOCKED them and put their
   * matches back in your feed. A block is a safety decision — only an explicit
   * unblock (removeContact) may undo it.
   *
   * The caller passes the status it already has in hand (the contacts list is
   * loaded on every screen that offers this action), which keeps the guard one
   * write instead of a read-then-write race.
   */
  save(
    ownerUid: string,
    contactUid: string,
    opts: { existingStatus?: ContactStatus | null } = {},
  ): Promise<void> {
    if (opts.existingStatus === 'blocked') return Promise.resolve()
    return this.set(
      contactDocId(ownerUid, contactUid),
      { ownerUid, contactUid, status: 'active' },
      { setCreatedAt: true },
    )
  }

  /** Block — upserts, so you can block someone you never saved. */
  block(ownerUid: string, contactUid: string): Promise<void> {
    return this.set(
      contactDocId(ownerUid, contactUid),
      { ownerUid, contactUid, status: 'blocked' },
      { setCreatedAt: true },
    )
  }

  /** Remove entirely (un-save / un-block). */
  removeContact(ownerUid: string, contactUid: string): Promise<void> {
    return this.remove(contactDocId(ownerUid, contactUid))
  }
}

export const contactsRepository = new ContactsRepository()
